import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { parseFullWidthToHalfWidth } from "@lhd/utility";
import {
  Cell,
  CellTemplate,
  Compatible,
  Uncertain,
  UncertainCompatible,
  getCellProperty,
  getCharFromKeyCode,
  isAlphaNumericKey,
  isNavigationKey,
  keyCodes,
} from "@silevis/reactgrid";
import { Input } from "antd";
import { isNaN } from "lodash";
import { ReactNode } from "react";
import { CellCustomType } from "../../type";

export class NumberCellTemplate implements CellTemplate<CellCustomType> {
  getCompatibleCell(
    uncertainCell: Uncertain<CellCustomType>,
  ): Compatible<CellCustomType> {
    const uncertainCellAs = uncertainCell as CellCustomType;
    return {
      value: NaN,
      ...uncertainCellAs,
    };
  }

  handleKeyDown(
    cell: Compatible<CellCustomType>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<CellCustomType>; enableEditMode: boolean } {
    const char = getCharFromKeyCode(keyCode, shift);
    const isIME = keyCode === 229 || keyCode === 299;
    if (cell.nonEditable) {
      return {
        cell,
        enableEditMode: false,
      };
    }
    if (isIME) {
      return {
        cell,
        enableEditMode: true,
      };
    }

    if (ctrl || alt || (shift && keyCode === keyCodes.SPACE)) {
      return {
        cell,
        enableEditMode:
          keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
      };
    }
    const editValue =
      typeof cell.valueSubmit === "string"
        ? cell.valueSubmit.replaceAll(cell.appPart.unit_of_measure, "").trim()
        : "";

    // Thousands separators make Number() return NaN, so strip them before every
    // numeric check. Without this, entering edit mode on a formatted value such
    // as "1,111" cleared the cell instead of showing the current amount.
    const toNumeric = (input?: string) => (input ?? "").replaceAll(",", "");

    const value = isNaN(Number(toNumeric(editValue)))
      ? ""
      : editValue + (char || "");

    if (!isNaN(Number(toNumeric(value)))) {
      cell.onChange?.();
      return {
        cell: {
          ...cell,
          text: value,
          valueSubmit: toNumeric(value),
        },
        enableEditMode: true,
      };
    }

    return {
      cell,
      enableEditMode: false,
    };
  }
  getClassName(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
  ): string {
    return cell.className || "";
  }

  update(
    cell: Compatible<CellCustomType>,
    cellToMerge: UncertainCompatible<CellCustomType>,
  ): Compatible<CellCustomType> {
    return this.getCompatibleCell({
      ...cell,
      text: cellToMerge.text,
      valueSubmit: cellToMerge.valueSubmit,
    });
  }

  refInput: any | null = null;
  refInputValue = "";
  typing = false;
  idTimeOut: NodeJS.Timeout | null = null;
  unit = null;
  isComposing = false;

  render(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void,
  ): ReactNode {
    const { isOdd } = cell as any;
    if (!isInEditMode) {
      return (
        <div className={isOdd ? "old_custom" : "even_custom"}>
          {cell.valueSubmit}
        </div>
      );
    }

    const editValue =
      typeof cell.valueSubmit === "string"
        ? cell.valueSubmit.replaceAll(cell.appPart.unit_of_measure, "").trim()
        : "";

    const handleInputChange = (value: string, commit = false) => {
      const convertedValue = parseFullWidthToHalfWidth(value);

      const stringValue = convertedValue.replaceAll(/,/g, "");

      let numericValue: string;
      if (stringValue === "" || stringValue === "-") {
        numericValue = "";
      } else {
        numericValue = stringValue;

        if (isNaN(numericValue)) {
          numericValue = "";
        }
      }

      const updatedCell = this.getCompatibleCell({
        ...cell,
        text: convertedValue,
        valueSubmit: numericValue,
      });

      onCellChanged(updatedCell, commit);

      if (this.refInput) {
        this.refInput.value = convertedValue;
      }
    };

    return (
      <Input
        className={isOdd ? "old_custom" : "even_custom"}
        inputMode="numeric"
        ref={(input) => {
          setTimeout(() => {
            input?.focus();
          }, 0);
          this.refInput = input;
        }}
        disabled={cell.nonEditable}
        bordered={false}
        style={{
          width: "100%",
          textAlign: "right",
          height: "100%",
          minHeight: "22px",
        }}
        defaultValue={editValue}
        onCompositionStart={() => {
          this.isComposing = true;
        }}
        onCompositionEnd={(e: any) => {
          this.isComposing = false;
          handleInputChange(e.target.value);
        }}
        onBlur={(e) => {
          handleInputChange(e.target.value, true);
        }}
        onKeyDownCapture={(e) => {
          this.typing = true;
          this.idTimeOut && clearTimeout(this.idTimeOut);
          this.idTimeOut = setTimeout(() => {
            this.typing = false;
          }, 50);

          const key = e.key;

          if (key === "Enter" && this.isComposing) {
            this.isComposing = false;
            handleInputChange(e.currentTarget.value);
            e.preventDefault();
            return;
          }

          const allowedKeys = [
            "0",
            "1",
            "2",
            "3",
            "4",
            "5",
            "6",
            "7",
            "8",
            "9",
            ".",
            "-",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Backspace",
            "Delete",
            "Tab",
            "Enter",
            "Escape",
            "Home",
            "End",
          ];

          if (e.ctrlKey && ["a", "c", "v", "x"].includes(key)) {
            return;
          }

          if (!allowedKeys.includes(key)) {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          cell.onChange?.(e);
          const value = e.target.value;
          handleInputChange(value);
        }}
        onInput={(e) => {
          this.refInputValue = this.typing
            ? this.refInput?.value || ""
            : this.refInputValue;
        }}
        onCopy={(e) => e.stopPropagation()}
        onCut={(e) => e.stopPropagation()}
        onPaste={(e) => {
          e.stopPropagation();
          const clipboardData = e.clipboardData;
          if (clipboardData) {
            const pastedData = clipboardData.getData("text");
            const convertedData = parseFullWidthToHalfWidth(pastedData);
            const cleanedValue = convertedData.replace(/[^\d.,-]/g, "");

            const inputElement = e.target as HTMLInputElement;
            const start = inputElement.selectionStart || 0;
            const end = inputElement.selectionEnd || 0;

            const currentValue = inputElement.value;
            const newValue =
              currentValue.substring(0, start) +
              cleanedValue +
              currentValue.substring(end);

            this.refInput.value = newValue;
            this.refInput.dispatchEvent(new Event("input", { bubbles: true }));

            e.preventDefault();
          }
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" && this.isComposing) {
            this.isComposing = false;
            handleInputChange(e.currentTarget.value);
          }

          if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode))
            e.stopPropagation();
        }}
      />
    );
  }
}
